import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { fetchAuthSession } from "aws-amplify/auth"
import { AppUserPUTFailReason } from "ipctg-inflow-types/InFlowEnums"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Label from "@/globalComponents/atoms/Label"
import Text from "@/globalComponents/atoms/Text"
import TextField from "@/globalComponents/molecules/TextField"
import { handleAuthError } from "@/utilities/auth-error-handler"
import { ExpectedError, GatedAppUserError } from "@/utilities/expectedErrors"
import { phoneValidationRule, requiredRule } from "@/utilities/form"
import { getAreaHierarchyIDs } from "@/utilities/getAreaHierarchyIDs"
import { meQueryOptions } from "@/utilities/hooks/queries/user"
import { useScopedNavigate } from "@/utilities/hooks/useScopedRouting"
import { useTheme } from "@/utilities/hooks/useTheme"

export const Route = createFileRoute(
  "/_authenticated/account/personal-information"
)({
  staticData: {
    pageName: "Personal Information",
  },
  component: PersonalInformationRouteComponent,
})

interface PersonalInfoFormSchema {
  firstName: string
  lastName: string
  phone: string
}

export function PersonalInformationRouteComponent() {
  const { t } = useTranslation(["errors", "common"])
  const scopedNavigate = useScopedNavigate()
  const queryClient = useQueryClient()
  const { data: me } = useSuspenseQuery(meQueryOptions())
  const { locationInformation } = useTheme()
  const locationID = locationInformation?.locationID

  const form = useForm<PersonalInfoFormSchema>({
    defaultValues: {
      firstName: me?.inflowUser.first,
      lastName: me?.inflowUser.last,
      phone: me?.inflowUser.phone,
    },
    mode: "onBlur",
  })

  const { mutate, isPending } = useMutation({
    mutationFn,
    onSuccess,
    onError,
  })

  return (
    <Card>
      <Heading level={2}>
        {t("account.personalInfo.header", { ns: "common" })}
      </Heading>
      <Text style={{ marginTop: "var(--space-2)" }}>
        {t("account.personalInfo.descriptionPrefix", { ns: "common" })}
        &nbsp;
        <strong>{t("account.personalInfo.brand", { ns: "common" })}</strong>
        &nbsp;
        {t("account.personalInfo.descriptionSuffix", { ns: "common" })}
      </Text>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        aria-label={t("account.personalInfo.label", { ns: "common" })}
      >
        <fieldset disabled={isPending}>
          <div className="form-row">
            <Controller<PersonalInfoFormSchema>
              name="firstName"
              control={form.control}
              rules={{
                required: requiredRule(t("common.required", { ns: "errors" })),
              }}
              render={({
                field: { ref, ...rest },
                fieldState: { error, invalid },
              }) => (
                <TextField {...rest} isInvalid={invalid}>
                  <Label>
                    {t("account.personalInfo.firstName", { ns: "common" })}
                  </Label>
                  <TextField.Input
                    innerRef={ref}
                    aria-label={t("account.personalInfo.firstName", {
                      ns: "common",
                    })}
                  />
                  <FieldError>{error?.message}</FieldError>
                </TextField>
              )}
            />
            <Controller<PersonalInfoFormSchema>
              name="lastName"
              control={form.control}
              rules={{ required: requiredRule(t("common.required")) }}
              render={({
                field: { ref, ...rest },
                fieldState: { error, invalid },
              }) => (
                <TextField {...rest} isInvalid={invalid}>
                  <Label>
                    {t("account.personalInfo.lastName", { ns: "common" })}
                  </Label>
                  <TextField.Input
                    innerRef={ref}
                    aria-label={t("account.personalInfo.lastName", {
                      ns: "common",
                    })}
                  />
                  <FieldError>{error?.message}</FieldError>
                </TextField>
              )}
            />
          </div>
          <Controller<PersonalInfoFormSchema>
            name="phone"
            control={form.control}
            rules={{
              validate: phoneValidationRule(t),
            }}
            render={({
              field: { ref, ...rest },
              fieldState: { error, invalid },
            }) => (
              <TextField {...rest} isInvalid={invalid}>
                <Label>
                  {t("account.personalInfo.phoneNumber", { ns: "common" })}
                </Label>
                <TextField.PhoneWithCountrySelect
                  innerRef={ref}
                  defaultCountry="US"
                  aria-label={t("account.personalInfo.phoneNumber", {
                    ns: "common",
                  })}
                />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          {form.formState.errors.root && (
            <FieldError className="center">
              {form.formState.errors.root.message}
            </FieldError>
          )}
          <Button buttonType="primary" type="submit" isPending={isPending}>
            {t("account.personalInfo.save", { ns: "common" })}
          </Button>
        </fieldset>
      </form>
    </Card>
  )

  function onSubmit(data: PersonalInfoFormSchema) {
    mutate(data)
  }

  async function mutationFn(data: PersonalInfoFormSchema) {
    const session = await fetchAuthSession()
    const idToken = session.tokens?.idToken?.toString()

    if (!idToken || !me) {
      throw new Error("Missing authentication token or user")
    }

    // This endpoint syncs the matching Cognito attributes, so we don't call
    // Cognito directly
    const rsp = await api.appUser.PUT({
      cognitoIDToken: idToken,
      ...me.inflowUser,
      phone: data.phone,
      first: data.firstName,
      last: data.lastName,
      locationID: locationID || null,
      enterpriseID: locationID
        ? getAreaHierarchyIDs({ locationID }).enterpriseID
        : null,
    })

    if ("failReason" in rsp) {
      throw new GatedAppUserError(rsp.failReason)
    }
  }

  function onSuccess() {
    queryClient.invalidateQueries({
      queryKey: meQueryOptions().queryKey,
      refetchType: "active",
    })
    scopedNavigate({ to: "/account" })
  }

  function onError(error: Error) {
    if (error instanceof ExpectedError) {
      // PhoneNumberInUse belongs on the phone field so it clears when the user
      // edits the number — a root error would linger and contradict the field
      const field =
        error.failReason === AppUserPUTFailReason.PhoneNumberInUse
          ? "phone"
          : "root"
      form.setError(field, {
        message: t(error.translationKey, { ns: "errors" }),
      })
    } else {
      handleAuthError(error, form, t)
    }
  }
}
